import React, { useEffect, useRef, useState } from 'react';

import { Button, Empty, Popconfirm, Select, Table } from 'antd';

import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import { UserRoleEnums, roleOptions } from '@/Interfaces/I_userRole';

import { getUserRole, putUserRole } from '@/Redux/slices/UserRoleSlice';

import UserRoleAccountModal from '@/Components/UserRoleAccountModal';

import UserRoleRadioOptions from './UserRoleRadioOptions';
import { ContainerOutlined, FileTextOutlined } from '@ant-design/icons';
import { getStreamerData, updateStreamerData, getMyPageProfileData, updateMyPageProfileData } from '@/Redux/slices/UserRoleSlice';
import StreamDetailModal, { StreamDetail } from '@/Components/StreamDetailModal';
import MyPageProfileDetailModal, { MyPageProfileDetail } from '@/Components/MyPageProfileDetailModal';
import { UpdateStreamDataRequest, UpdateMyPageDataRequest } from '@/Interfaces/I_userRole';

const UserRole = () => {
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddNewRoleVisible, setIsAddNewRoleVisible] = useState(false);
  const selectRole = useRef<string>('');
  const userRoleLoading = useAppSelector((state) => state.userRole.loading);
  const userRoleData = useAppSelector((state) => state.userRole.value?.userRoles?.data);
  const pagination = useAppSelector((state) => state.userRole.value.userRoles.pagination);
  const [streamDetailModalOpen, setStreamDetailModalOpen] = useState(false);
  const [myPageProfileDetailModalOpen, setMyPageProfileDetailModalOpen] = useState(false);
  const [selectedUserUuid, setSelectedUserUuid] = useState<string>('');

  useEffect(() => {
    dispatch(getUserRole({}));
  }, [dispatch]);

  const onStreamDetailClick = (uuid: string) => {
    dispatch(getStreamerData(uuid));
    setSelectedUserUuid(uuid);
    setStreamDetailModalOpen(true);
  };
  
  const onMyPageProfileDetailClick = (uuid: string) => {
    dispatch(getMyPageProfileData(uuid));
    setSelectedUserUuid(uuid);
    setMyPageProfileDetailModalOpen(true);
  };

  const columns = [
    {
      title: 'No.',
      dataIndex: 'no',
      key: 'no',
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      render: (text: string) => <p className="h-6 line-clamp-1 break-all m-0">{text}</p>,
      width: '20%',
    },
    {
      title: 'Stream Details',
      dataIndex: 'streamDetails',
      key: 'streamDetails',
      render: (text: string, { key }: any) => (
              <div className="flex items-center">
                <p className="h-6 line-clamp-1 break-all m-0">{text}</p>
                <FileTextOutlined className="cursor-pointer" onClick={() => onStreamDetailClick(key)} />
              </div>
            ),
    },
    {
      title: 'MyPage Profile',
      dataIndex: 'myPageProfile',
      key: 'myPageProfile',
      render: (text: string, { key }: any) => (
              <div className="flex items-center">
                <p className="h-6 line-clamp-1 break-all m-0">{text}</p>
                <ContainerOutlined className="cursor-pointer" onClick={() => onMyPageProfileDetailClick(key)} />
              </div>
            ),
    },
    {
      title: 'User PKey',
      dataIndex: 'hybrisId',
      key: 'hybrisId',
    },
    {
      title: 'Role',
      dataIndex: 'userRole',
      key: 'userRole',
      width: '40%',
      render: (userRole: { role: UserRoleEnums; userPk: string }) => {
        const { role, userPk } = userRole;
        return <UserRoleRadioOptions userPk={userPk} userRole={role} />;
      },
    },
    {
      title: '',
      dataIndex: 'resetButton',
      key: 'resetButton',
      render: (resetParam: { userPk: string; resetOnClick: (userPk: string) => void }) => {
        const { userPk, resetOnClick } = resetParam;
        return (
          userPk && ( 
            <div className="flex justify-between items-center">
              <div style={{ width: 77.5 }} className="flex justify-center">
                <Popconfirm
                  title="Reset to Normal Customer?"
                  onConfirm={() => resetOnClick(userPk)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger>
                    RESET
                  </Button>
                </Popconfirm>
              </div>
            </div>
          )
        );
      },
      width: '10%',
    },
  ];

  const slicedRoleOptions = roleOptions.filter((role) => role.value != UserRoleEnums.CUSTOMER);

  const resetOnClick = (userPk: string) => {
    dispatch(putUserRole([{ uuid: userPk, kocUserRole: 'CUSTOMER' }]));
  };

  const updateStreamerInformation = (formValues: StreamDetail) => {
    const data: UpdateStreamDataRequest = {
      uuid: selectedUserUuid,
      name: formValues.name ?? null,
      description: formValues.description ?? null,
      banner: (formValues.banner?.[0] as any)?.originFileObj ?? formValues.banner?.[0] ?? null,
      isBannerDelete: formValues.isBannerDelete,
      snsPlatforms: formValues.snsPlatforms,
    };

    dispatch(updateStreamerData(data));
  };

  const updateMyPageInformation = (formValues: MyPageProfileDetail) => {
    const data: UpdateMyPageDataRequest = {
      uuid: selectedUserUuid,
      profileID: formValues.profileID ?? null,
      name: formValues.name ?? null,
      description: formValues.description ?? null,
      profileIconId: formValues.profileIconId ?? null,
      profileIconUrl: formValues.profileIconUrl ?? null,
    };

    dispatch(updateMyPageProfileData(data));
  };

  return (
    <div className="h-auto bg-white p-6 pb-2">
      <div className="flex justify-between">
        <Select
          defaultValue={slicedRoleOptions[0].value}
          style={{ width: 300 }}
          options={slicedRoleOptions}
          onChange={(value) => {
            selectRole.current = value !== "ALL" ? value : '';
            dispatch(getUserRole({ role: selectRole.current }));
          }}
        />
        <Button
          type="primary"
          onClick={() => {
            setIsAddNewRoleVisible(true);
          }}
        >
          Update Role
        </Button>
      </div>
      {!userRoleLoading && userRoleData?.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" />
      ) : (
        <Table
          className="mt-4"
          columns={columns}
          loading={userRoleLoading}
          dataSource={userRoleData?.map((data, index: number) => ({
            key: data.uuid,
            no: pagination.page * pagination.pageSize + index + 1,
            userName: data.name,
            hybrisId: data.hybrisId,
            userRole: {
              role: data.kocUserRole,
              userPk: data.uuid,
            },
            resetButton: {
              userPk: data.uuid,
              resetOnClick,
            },
          }))}
          pagination={{
            total: pagination ? pagination.total : 0,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            defaultPageSize: pageSize,
            defaultCurrent: 1,
            showSizeChanger: true,
            current: currentPage + 1,
            onChange: (page: number, selectPageSize: number) => {
              setCurrentPage(page - 1);
              dispatch(
                getUserRole({
                  role: selectRole.current,
                  page_no: page - 1,
                  page_size: selectPageSize,
                }),
              );
            },
            onShowSizeChange: (current, size) => {
              setPageSize(size);
            },
          }}
        />
      )}
      <UserRoleAccountModal
        isAddNewRoleVisible={isAddNewRoleVisible}
        setIsAddNewRoleVisible={setIsAddNewRoleVisible}
      />
      <StreamDetailModal
        open={streamDetailModalOpen}
        onConfirm={updateStreamerInformation}
        setModalOpen={setStreamDetailModalOpen}
      />
      <MyPageProfileDetailModal
        open={myPageProfileDetailModalOpen}
        onConfirm={updateMyPageInformation}
        setModalOpen={setMyPageProfileDetailModalOpen}
      />
    </div>
  );
};

export default UserRole;
